<script setup lang="ts">
import { ref, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { AIRPORT, STATUS_OPEN } from '@/constants';
import { useCityStore } from '@/store/CityStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import BrochureSelect from '@/components/BrochureSelect.vue';
import CityApplication from '@/components/city/CityApplication.vue';
import CityInfo from '@/components/city/CityInfo.vue';

const dialogRef = inject('dialogRef') as Ref;
const cityData = dialogRef?.value?.options.data.cityData;
const onApply = dialogRef?.value?.options.emits.onApply;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const cityStore = useCityStore();

const initCity = () => {
  return {
    airportCode: '',
    airportNameEn: '',
    airportNameFr: '',
    cityNameEn: '',
    cityNameFr: '',
    regionEn: '',
    regionFr: '',
    countryNameEn: '',
    countryNameFr: '',
    brochure: '',
    destSvId: null,
    parentDestId: null,
    gatewayStatus: STATUS_OPEN,
    destinationStatus: STATUS_OPEN,
    destinationApplicability: {
      isAir: false,
      isHotel: false,
      isPackage: false,
      isTour: false,
      isCruise: false
    },
    gatewayApplicability: {
      isAir: false,
      isHotel: false,
      isPackage: false,
      isTour: false,
      isCruise: false
    },
    referenceType: AIRPORT
  };
};

const cityInfo = ref(cityData ? cloneDeep(cityData) : initCity());
const isLoading = ref(false);

const rules = ref({
  airportCode: { required },
  referenceType: { required },
  airportNameEn: { required },
  airportNameFr: { required },
  cityNameEn: { required },
  cityNameFr: { required },
  regionEn: { required },
  regionFr: { required },
  countryNameEn: { required },
  countryNameFr: { required }
});
const v$: any = useVuelidate(rules.value, cityInfo.value);

const saveCityData = (handleCall: any) => {
  isLoading.value = true;
  handleCall
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      onApply();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleSubmit = async () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    const payload = {
      ...cityInfo.value,
      destSvId: cityInfo.value?.destSvId || null,
      parentDestId: cityInfo.value?.parentDestId || null,
      airportCode: cityInfo.value?.airportCode?.toUpperCase()
    };
    if (cityData) {
      const oldCompositeKey = {
        pk: cityInfo.value?.pk,
        sk: cityInfo.value?.sk
      };
      const cityInfoUpdate = { data: payload, oldCompositeKey };
      saveCityData(cityStore.updateCity(cityInfoUpdate));
    } else {
      saveCityData(cityStore.addCity(payload));
    }
  }
};
</script>
<template>
  <div class="city-modal">
    <CityInfo v-model:cityInfo="cityInfo" :validate="v$" />
    <div>
      <div class="applicable_to">{{ $t('management.air.applicable_to') }} <span class="text-primary">*</span></div>
      <CityApplication v-model:cityApplicability="cityInfo.gatewayApplicability" v-model:cityStatus="cityInfo.gatewayStatus" :title="$t('management.air.gateway_status')" />
      <CityApplication v-model:cityApplicability="cityInfo.destinationApplicability" v-model:cityStatus="cityInfo.destinationStatus" :title="$t('management.air.destination_status')" />
    </div>
    <Fluid>
      <div class="field grid">
        <div class="col-6">
          <BrochureSelect v-model:brochure="cityInfo.brochure" />
        </div>
      </div>
    </Fluid>
    <div class="text-center mt-5">
      <Button data-testid="submit-btn" :loading="isLoading" :label="$t('common.buttons.submit')" @click="handleSubmit()"></Button>
    </div>
  </div>
</template>
